import React, { useEffect, useState } from 'react';
import { DollarSign, TrendingUp, Users, AlertTriangle, RefreshCw, Loader2, CalendarClock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

const Kpi = ({ label, value, sub, icon: Icon, tone = '' }) => (
  <div className="rounded-xl border bg-card p-4" data-testid={`revenue-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
      {Icon && <Icon className="w-3 h-3" />} {label}
    </div>
    <div className={`text-2xl font-semibold mt-1 ${tone}`}>{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

const KIND_LABEL = { subscription_start: 'New sub', renewal: 'Renewal', plan_change: 'Plan change', payment: 'Payment' };

export default function RevenueDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setData(await api.getRevenueDashboard()); }
    catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }
  if (!data) return null;

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="revenue-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Revenue Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-0.5">Platform-wide subscription revenue and health.</p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="revenue-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <Kpi label="MRR" value={fmt(data.mrr)} icon={DollarSign} />
        <Kpi label="ARR" value={fmt(data.arr)} icon={TrendingUp} />
        <Kpi label="Active subs" value={data.active_subscriptions} sub={`${data.canceling} canceling · ${data.past_due} past due`} icon={Users} tone={data.past_due > 0 ? 'text-rose-500' : ''} />
        <Kpi label="MRR at risk" value={fmt(data.mrr_at_risk)} icon={AlertTriangle} tone={data.mrr_at_risk > 0 ? 'text-amber-500' : ''} />
        <Kpi label="Churned" value={data.churned} sub="tenants back on Free" icon={AlertTriangle} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 rounded-xl border bg-card p-5" data-testid="revenue-chart">
          <h2 className="text-sm font-semibold mb-4">Collected revenue by month</h2>
          {data.monthly_revenue.length === 0 ? (
            <p className="text-xs text-muted-foreground py-10 text-center">No paid transactions yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.monthly_revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.1} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
                <Tooltip formatter={(v) => fmt(v)} labelStyle={{ color: '#111' }} />
                <Bar dataKey="revenue" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="rounded-xl border bg-card p-5" data-testid="plan-breakdown">
          <h2 className="text-sm font-semibold mb-4">Plan mix</h2>
          <div className="space-y-3">
            {data.plan_breakdown.length === 0 && <p className="text-xs text-muted-foreground">No paid tenants.</p>}
            {data.plan_breakdown.map((b) => (
              <div key={`${b.plan}_${b.cycle}`} className="flex items-center justify-between text-sm">
                <span className="capitalize">{b.plan} <span className="text-xs text-muted-foreground">({b.cycle})</span></span>
                <span className="tabular-nums">{b.count} · <span className="font-medium">{fmt(b.mrr)}/mo</span></span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border overflow-hidden" data-testid="upcoming-renewals">
          <div className="px-4 py-3 border-b flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold">Upcoming renewals</h2>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="text-left font-medium px-4 py-2">Tenant</th>
                <th className="text-left font-medium px-2 py-2">Plan</th>
                <th className="text-right font-medium px-2 py-2">Amount</th>
                <th className="text-left font-medium px-4 py-2 pl-4">Renews</th>
              </tr>
            </thead>
            <tbody>
              {data.upcoming_renewals.map((r) => (
                <tr key={r.tenant_id} className="border-t" data-testid="renewal-row">
                  <td className="px-4 py-2">{r.tenant_name}</td>
                  <td className="px-2 py-2 capitalize">{r.plan} <span className="text-muted-foreground">({r.cycle}, {r.seats} seats)</span></td>
                  <td className="px-2 py-2 text-right tabular-nums">{fmt(r.amount)}</td>
                  <td className="px-4 py-2 pl-4">
                    {fmtDate(r.renews_at)}
                    {r.status === 'canceling' && <span className="ml-1.5 text-amber-500">(ends)</span>}
                    {r.status === 'past_due' && <span className="ml-1.5 text-rose-500">(past due)</span>}
                  </td>
                </tr>
              ))}
              {data.upcoming_renewals.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">No paid tenants.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="rounded-xl border overflow-hidden" data-testid="recent-payments">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-semibold">Recent payments</h2>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="text-left font-medium px-4 py-2">Date</th>
                <th className="text-left font-medium px-2 py-2">Tenant</th>
                <th className="text-left font-medium px-2 py-2">Type</th>
                <th className="text-right font-medium px-4 py-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_transactions.map((t, i) => (
                <tr key={i} className="border-t" data-testid="payment-row">
                  <td className="px-4 py-2">{fmtDate(t.created_at)}</td>
                  <td className="px-2 py-2">{t.tenant_id}</td>
                  <td className="px-2 py-2">{KIND_LABEL[t.kind] || t.kind} <span className="text-muted-foreground capitalize">({t.plan_id}, {t.cycle})</span></td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmt(t.amount)}</td>
                </tr>
              ))}
              {data.recent_transactions.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">No payments yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
